import cloneDeep from 'lodash.clonedeep'
import { toCamelCase, toPascalCase } from './stringFn';

/**
 * Finds and returns the deep value at the given path for the provided object.
 * @param {Object} obj - The object to iterate.
 * @param {string} path - The property path.
 * @param {string} [delimiter='/'] - The delimiter to use in the property path. Defaulting to '/'. (optional)
 */
export function findValue(obj, path, delimiter) {
    let result = cloneDeep(Object(obj));

    for (let i = 0, paths = path.split(delimiter || '/'), len = paths.length; i < len; i++) {
        result = result && paths && result[paths[i]];
    }

    return result;
}

/**
 * Look up the key of an object by its value
 * @param {Object} obj - The target object
 * @param {any} value - The value to look up 
 * @returns The the Object's key if found.
 */
export function getKeyByValue(obj, value) {
    return Object.keys(obj).find(key => obj[key] === value);
}

/**
 * Search and get the deep value in object by the given path. Paths are defined with a string 
 * using `.` or `[...]` as delimiters. E.g `'obj.property'` or `'obj[property]'`
 * @param {Object} obj - The source object for the search
 * @param {String} path - A string path that describes the value's location and its property name in the object.
 * @param {*} fallback - Fallback value if value is not found
 */
export function getObjectValueByPath (obj, path, fallback) {
    if (path === '' || (typeof path !== 'string' && typeof path !== 'number') || !isObject(obj)) return fallback;
    
    // Determine path
    path = String(path);
    path = path.replace(/\[(\w+)\]|\//g, '.$1'); // Convert indexes and/or paths to properties
    path = path.replace(/^\.|\.$/g, ''); // Strip a leading and/or trailing dot

    // Get the nested value by path
    const props = path.split('.');
    const lastIndex = props.length - 1;

    if (!~lastIndex) return obj === undefined ? fallback : obj;
    for (let i = 0; i < lastIndex; i++) {
        if (!isObject(obj) || obj[props[i]] === undefined) return fallback;
        obj = obj[props[i]];
    }

    return obj[props[lastIndex]] === undefined ? fallback : obj[props[lastIndex]];
}

/**
 * Checks if provided parameter is an object.
 * @param {Object} obj - The source object to check
 */
export function isObject(obj) {
    return obj === Object(obj);
}

/**
 * Checks if provided object is empty. The object is presumed empty when it doesn't contain any object entries.
 * @param {Object} obj - The source object to check 
 */
export function isEmpty(obj) {
    return Object.entries(obj).length === 0 && obj.constructor === Object;
}

/**
 * Camel casing all property keys for provided object.
 * @param {Object} obj - The source object to convert all properties to camel case.
 * @returns New object with all property keys converted to camel case.
 */
export const toCamelCaseProperties = obj => {
	return obj ? Object.assign({},
		...Array.from(
			Object.entries(obj).map(([key, value]) => [toCamelCase(key), value]),
			([key, value]) => ({ [key]: value })
		)
	) : obj;
};

/**
 * Pascal casing all property keys for provided object.
 * @param {Object} obj - The source object to convert all properties to pascal case.
 * @returns New object with all property keys converted to pascal case.
 */
export const toPascalCaseProperties = obj => {
	return obj ? Object.assign({},
		...Array.from(
			Object.entries(obj).map(([key, value]) => [toPascalCase(key), value]),
			([key, value]) => ({ [key]: value })
		)
	) : obj;
};